import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'day8-ui';
import { CurrencyDropdown } from 'day8-react/build/new-components';
import { CiMoneyBill } from '@react-icons/all-files/ci/CiMoneyBill';
import { CiShoppingBasket } from '@react-icons/all-files/ci/CiShoppingBasket';
import { PiInfo } from '@react-icons/all-files/pi/PiInfo';
import { CiLogin } from '@react-icons/all-files/ci/CiLogin';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import history from '../../history';
import { logoutUser } from '../../actions/user';
import { fetchAccountData } from '../../actions/account';
import { useBookingBasket } from '../../context/BookingBasketContext';
import { useInfoDrawer } from '../../context/InfoDrawerContext';
import { useMobileAppNav } from '../../context/MobileAppNavContext';
import BookingTimeout from '../booking/BookingTimeout';
import FunnelInfo from '../InfoDrawer/FunnelInfo/FunnelInfo';
import CookieSettingsButton from '../CookieSettingsButton';
import {
  getAppDrawerNavItems,
  getAccountCompletenessHint,
  resolveMobileDrawerActiveKey,
  getAccountNavItemClass,
  ACCOUNT_NAV_ARROW_CLASS
} from '../../helpers/accountOverview';

const MobileAppDrawer = ({ supportedCurrencies, isYachtsAndFriends }) => {
  const dispatch = useDispatch();
  const { drawerOpen, closeDrawer, drawerConfig } = useMobileAppNav();
  const { shouldShowCheckout } = useBookingBasket();
  const { openInfoDrawer } = useInfoDrawer();
  const isFunnelYachtsRedirectFeature = useFeatureIsOn('custom-yachts-redirect');
  const loggedUser = useSelector(state => state.user.loggedUser);
  const account = useSelector(state => state.account);
  const [currentPath, setCurrentPath] = useState(history.location.pathname);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [drawerOpening, setDrawerOpening] = useState(false);
  const dragStartX = useRef(null);
  const dragXRef = useRef(0);

  useEffect(() => {
    const unlisten = history.listen(location => setCurrentPath(location.pathname));
    return unlisten;
  }, []);

  useEffect(() => {
    if (!drawerOpen) {
      setDrawerOpening(false);
      return undefined;
    }
    setDrawerOpening(true);
    const t = setTimeout(() => setDrawerOpening(false), 300);
    return () => clearTimeout(t);
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen || !loggedUser) return;
    if (typeof account?.profileCompleteness !== 'number' && !account?.fetching) {
      dispatch(fetchAccountData());
    }
  }, [drawerOpen, loggedUser, account?.profileCompleteness, account?.fetching, dispatch]);

  if (!drawerOpen) return null;

  const activeKey = resolveMobileDrawerActiveKey(currentPath, drawerConfig.activeKey);
  const isAmbassador = drawerConfig.isAmbassador || false;
  const showBrowseInfo = currentPath.startsWith('/browse');
  const isPaymentPage = currentPath === '/booking/payment';
  const redirectPathOnLogout = currentPath.startsWith('/account') ? '/account/login' : null;

  const completenessPct = drawerConfig.showCompleteness === false? undefined: (drawerConfig.completenessPct != null? drawerConfig.completenessPct: (typeof account?.profileCompleteness === 'number' ? account.profileCompleteness : undefined));

  const completenessHint = drawerConfig.showCompleteness === false? undefined: getAccountCompletenessHint(account, completenessPct, drawerConfig.completenessHint);

  const navItems = loggedUser? getAppDrawerNavItems({ activeKey, isAmbassador, showBrowseInfo }): [];

  const closeMenu = () => {
    closeDrawer();
    setDragX(0);
    dragXRef.current = 0;
    setDragging(false);
  };

  const handleNavTo = to => e => {
    e.preventDefault();
    closeMenu();
    history.push(to);
  };

  const handleLogout = () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    dispatch(logoutUser(redirectPathOnLogout));
  };

  const renderDrawerItem = item => {
    if (item.type === 'currency') {
      return (
        <div key={item.key} className={`${getAccountNavItemClass(item, { drawer: true })} yw-account-drawer__currency`}>
          <CurrencyDropdown
            className="!dui-w-full !dui-justify-between !dui-p-0 !dui-bg-transparent hover:!dui-bg-transparent"
            buttonType="TERTIARY"
            leftIcon={<CiMoneyBill />}
            buttonSize="SMALL"
            supportedCurrencies={supportedCurrencies}
            toast={toast}
          />
        </div>
      );
    }

    const cls = getAccountNavItemClass(item, { drawer: true });
    const content = (
      <>
        <span className={item.action === 'info' ? 'dui-flex dui-items-center dui-gap-2' : undefined}>
          {item.action === 'info' && <PiInfo />}
          <span>{item.label}</span>
        </span>
        {item.hasArrow && <span className={ACCOUNT_NAV_ARROW_CLASS}>{item.arrow}</span>}
      </>
    );

    if (item.action === 'logout') {
      return (
        <button
          key={item.key}
          type="button"
          className={`${cls} dui-w-full`}
          style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
          onClick={handleLogout}
          disabled={isLoggingOut}
        >
          <span>{isLoggingOut ? 'Logging out...' : item.label}</span>
          {item.hasArrow && <span className={ACCOUNT_NAV_ARROW_CLASS}>{item.arrow}</span>}
        </button>
      );
    }

    if (item.action === 'info') {
      return (
        <button
          key={item.key}
          type="button"
          className={`${cls} dui-w-full`}
          style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
          onClick={() => {
            closeMenu();
            setTimeout(() => openInfoDrawer({ content: <FunnelInfo /> }), 50);
          }}
        >
          {content}
        </button>
      );
    }

    if (item.external && item.href) {
      return (
        <a
          key={item.key}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={cls}
          onClick={closeMenu}
        >
          {content}
        </a>
      );
    }

    return (
      <Link key={item.key} to={item.to} className={cls} onClick={handleNavTo(item.to)}>
        {content}
      </Link>
    );
  };

  const drawerSwipeHandlers = {
    onTouchStart: e => {
      setDrawerOpening(false);
      dragStartX.current = e.touches[0].clientX;
      setDragging(true);
      setDragX(0);
      dragXRef.current = 0;
    },
    onTouchMove: e => {
      if (dragStartX.current == null) return;
      const dx = Math.max(0, e.touches[0].clientX - dragStartX.current);
      dragXRef.current = dx;
      setDragX(dx);
    },
    onTouchEnd: () => {
      const dx = dragXRef.current;
      dragStartX.current = null;
      if (dx > 80) closeMenu();
      else {
        setDragX(0);
        dragXRef.current = 0;
        setDragging(false);
      }
    }
  };

  const drawerStyle = {
    transform: `translateX(${dragX}px)`,
    transition: dragging ? 'none' : 'transform 220ms cubic-bezier(0.22,1,0.36,1)',
    animation: drawerOpening && !dragging ? 'yw-account-drawer-in 280ms cubic-bezier(0.22,1,0.36,1)' : undefined
  };

  const bookNowHref = isYachtsAndFriends? '/browse/yachtsandfriends': `/browse${isFunnelYachtsRedirectFeature ? '/yachts' : ''}`;

  return (
    <div className="dui-fixed dui-inset-0 dui-z-50 lg:dui-hidden">
      <style>
        {`
          @keyframes yw-account-drawer-in {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          @keyframes yw-account-fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
      <div
        role="presentation"
        onClick={closeMenu}
        style={{ animation: 'yw-account-fade-in 200ms ease-out' }}
        className="dui-absolute dui-inset-0 dui-bg-primary-950/60"
      />
      <div
        className="yw-account dui-absolute dui-right-0 dui-top-0 dui-flex dui-h-full dui-w-[85%] dui-max-w-[340px] dui-touch-none dui-flex-col dui-bg-white active:dui-cursor-grabbing"
        style={{
          ...drawerStyle,
          boxShadow: '-8px 0 30px rgba(18,47,81,0.25)'
        }}
        {...drawerSwipeHandlers}
      >
        <div className="dui-flex dui-shrink-0 dui-cursor-grab dui-items-center dui-justify-between dui-border-b-2 dui-border-solid dui-border-primary-950 dui-py-2 dui-pl-5 dui-pr-3">
          <span className="yw-account-drawer__title">My account</span>
          <button
            type="button"
            aria-label="Close menu"
            onClick={closeMenu}
            className="dui-flex dui-h-11 dui-w-11 dui-cursor-pointer dui-items-center dui-justify-center dui-border-0 dui-bg-transparent dui-text-primary-950"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {loggedUser && completenessPct != null && (
          <div className="dui-shrink-0 dui-border-b dui-border-solid dui-px-5 dui-py-4" style={{ borderColor: 'var(--yw-hairline-navy, rgba(18,47,81,0.14))' }}>
            <div className="dui-flex dui-items-baseline dui-justify-between">
              <span className="yw-account-drawer__footer-label">Profile complete</span>
              <span className="yw-account-drawer__footer-pct">{completenessPct}%</span>
            </div>
            <div className="yw-account-drawer__meter">
              <div
                className="yw-account-drawer__meter-fill"
                style={{ width: `${Math.min(100, Math.max(0, completenessPct))}%` }}
              />
            </div>
            {completenessHint && (
              <p className="yw-account-drawer__hint dui-mt-3">{completenessHint}</p>
            )}
          </div>
        )}

        <nav className="dui-relative dui-z-10 dui-flex-1 dui-overflow-y-auto dui-overscroll-contain">
          {loggedUser && shouldShowCheckout && (
            <button
              type="button"
              className={`${getAccountNavItemClass({ variant: 'primary', active: isPaymentPage }, { drawer: true })} dui-w-full dui-text-green-600`}
              style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
              onClick={() => {
                closeMenu();
                if (!isPaymentPage) history.push('/booking/payment');
              }}
            >
              <span className="dui-flex dui-items-center dui-gap-2">
                <CiShoppingBasket />
                Checkout (<BookingTimeout />)
              </span>
            </button>
          )}

          {loggedUser && navItems.map(renderDrawerItem)}

          {!loggedUser && (
            <>
              {showBrowseInfo && (
                <button
                  type="button"
                  className={`${getAccountNavItemClass({ variant: 'sub' }, { drawer: true })} dui-w-full`}
                  style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
                  onClick={() => {
                    closeMenu();
                    setTimeout(() => openInfoDrawer({ content: <FunnelInfo /> }), 50);
                  }}
                >
                  <span className="dui-flex dui-items-center dui-gap-2">
                    <PiInfo />
                    Info
                  </span>
                </button>
              )}
              <div className={`${getAccountNavItemClass({ variant: 'sub' }, { drawer: true })} yw-account-drawer__currency`}>
                <CurrencyDropdown
                  className="!dui-w-full !dui-justify-between !dui-p-0 !dui-bg-transparent hover:!dui-bg-transparent"
                  buttonType="TERTIARY"
                  leftIcon={<CiMoneyBill />}
                  buttonSize="SMALL"
                  supportedCurrencies={supportedCurrencies}
                  toast={toast}
                />
              </div>
              <button
                type="button"
                className={`${getAccountNavItemClass({ variant: 'sub' }, { drawer: true })} dui-w-full`}
                style={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
                onClick={() => {
                  closeMenu();
                  history.replace({
                    ...history.location,
                    state: {
                      returnTo: { pathname: history.location.pathname, search: history.location.search },
                      showLoginModal: true,
                      page: 'login'
                    }
                  });
                }}
              >
                <span className="dui-flex dui-items-center dui-gap-2">
                  <CiLogin />
                  Log in
                </span>
              </button>
            </>
          )}
        </nav>

        <div className="dui-shrink-0 dui-p-4">
          <Link
            to={bookNowHref}
            className="yw-app-btn yw-app-btn--teal dui-block dui-w-full dui-text-center dui-no-underline"
            onClick={closeMenu}
          >
            Book now
          </Link>
          {/* The desktop footer is hidden on mobile, so consent stays
              withdrawable from here. */}
          <div className="dui-mt-4 dui-text-center">
            <CookieSettingsButton onClick={closeMenu} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAppDrawer;
